import React, { useState } from 'react';
import { testRemoteLoading, debugRemoteEntry, type RemoteDebugInfo } from '../utils/remote-loader-debug';
import {
  diagnoseMFIssues,
  mimeTypeDiagnostic,
  type NetworkDiagnostic,
  type MimeTypeError,
} from '../utils/mime-type-diagnostic';
import { useLanguage } from '../contexts/language-context';
import { ThemeToggle, LanguageToggle } from '../components/theme-controls';
import { MimeDiagnostic } from '../components/mime-diagnostic';
import { QuickTest } from '../components/quick-test';
import { AdvancedOptions } from '../components/advanced-options';
import { RealTimeMonitor } from '../components/real-time-monitor';
import { getAdminRemoteEntryUrl } from '../utils/admin-url';

export function RemoteDiagnosticPage() {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<RemoteDebugInfo | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [mimeErrors, setMimeErrors] = useState<MimeTypeError[]>([]);
  const [networkDiagnostic, setNetworkDiagnostic] = useState<NetworkDiagnostic | null>(null);
  const [remoteUrl, setRemoteUrl] = useState(getAdminRemoteEntryUrl());
  const [hasQuickTestErrors, setHasQuickTestErrors] = useState(false);

  const originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
  };

  const captureConsole = () => {
    const captured: string[] = [];

    const formatArgs = (args: any[]) => {
      return args
        .map((arg) => {
          if (typeof arg === 'object' && arg !== null) {
            try {
              if (arg.url && arg.expected && arg.received) {
                return `URL: ${arg.url}, Expected: ${arg.expected}, Received: ${arg.received}`;
              } else if (arg.url && arg.status) {
                return `URL: ${arg.url}, Status: ${arg.status}, MIME: ${arg.mimeType || 'unknown'}`;
              } else if (arg.url && arg.mimeType) {
                return `URL: ${arg.url}, Status: ${arg.status || 'unknown'}, MIME: ${arg.mimeType}${
                  arg.redirected ? ' (redirected)' : ''
                }`;
              } else {
                return JSON.stringify(arg, null, 2);
              }
            } catch (e) {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');
    };

    console.log = (...args: any[]) => {
      captured.push(`[LOG] ${formatArgs(args)}`);
      originalConsole.log(...args);
    };

    console.error = (...args: any[]) => {
      captured.push(`[ERROR] ${formatArgs(args)}`);
      originalConsole.error(...args);
    };

    console.warn = (...args: any[]) => {
      captured.push(`[WARN] ${formatArgs(args)}`);
      originalConsole.warn(...args);
    };

    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      return captured;
    };
  };

  const runDiagnostic = async () => {
    setIsLoading(true);
    setLogs([]);
    setMimeErrors([]);
    setNetworkDiagnostic(null);

    const restoreConsole = captureConsole();

    try {
      mimeTypeDiagnostic.clearErrors();

      const info = await debugRemoteEntry(remoteUrl);
      setDebugInfo(info);

      const mimeResult = await diagnoseMFIssues(remoteUrl);
      setNetworkDiagnostic(mimeResult.diagnostics[0] || null);
      setMimeErrors(mimeResult.errors);

      await testRemoteLoading();

      const newErrors = mimeTypeDiagnostic.getErrors();
      setMimeErrors((prev) => [...prev, ...newErrors]);
    } catch (error) {
      console.error('Diagnostic failed:', error);
    } finally {
      const capturedLogs = restoreConsole();
      setLogs(capturedLogs);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-90 transition-colors">
      <div className="p-8 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-neutral-90 dark:text-white">{t('title')}</h1>
          <div className="flex gap-3">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
        <QuickTest onResultsUpdate={setHasQuickTestErrors} remoteUrl={remoteUrl} />
        <AdvancedOptions onRemoteUrlChange={setRemoteUrl} currentUrl={remoteUrl} />
        <div className="mb-6">
          <button
            onClick={runDiagnostic}
            disabled={isLoading}
            className={`px-6 py-2 rounded-pill transition-colors text-white ${
              hasQuickTestErrors
                ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                : 'bg-primary-60 hover:bg-primary-70 disabled:bg-primary-30'
            }`}
          >
            {isLoading ? t('runningDiagnostic') : t('runDiagnostic')}
            {hasQuickTestErrors && ' (Issues Detected)'}
          </button>
        </div>
        {debugInfo && (
          <div className="mb-6 p-4 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-white dark:bg-neutral-90">
            <h2 className="text-xl font-semibold mb-3 text-neutral-90 dark:text-white">{t('remoteDebugInfo')}</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('url')}:</strong> {debugInfo.url}
              </div>
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('accessible')}:</strong>{' '}
                <span
                  className={
                    debugInfo.accessible ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }
                >
                  {debugInfo.accessible ? `✅ ${t('yes')}` : `❌ ${t('no')}`}
                </span>
              </div>
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('cors')}:</strong>{' '}
                <span
                  className={
                    debugInfo.cors ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'
                  }
                >
                  {debugInfo.cors ? `✅ ${t('ok')}` : `⚠️ ${t('check')}`}
                </span>
              </div>
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('contentType')}:</strong>{' '}
                {debugInfo.contentType || t('na')}
              </div>
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('hasInit')}:</strong>{' '}
                <span
                  className={
                    debugInfo.hasInit ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }
                >
                  {debugInfo.hasInit ? `✅ ${t('yes')}` : `❌ ${t('no')}`}
                </span>
              </div>
              <div className="text-neutral-70 dark:text-neutral-30">
                <strong className="text-neutral-90 dark:text-white">{t('hasGet')}:</strong>{' '}
                <span
                  className={debugInfo.hasGet ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                >
                  {debugInfo.hasGet ? `✅ ${t('yes')}` : `❌ ${t('no')}`}
                </span>
              </div>
              {debugInfo.size && (
                <div className="text-neutral-70 dark:text-neutral-30">
                  <strong className="text-neutral-90 dark:text-white">{t('size')}:</strong> {debugInfo.size}{' '}
                  {t('bytes')}
                </div>
              )}
              {debugInfo.error && (
                <div className="col-span-2 text-neutral-70 dark:text-neutral-30">
                  <strong className="text-neutral-90 dark:text-white">{t('error')}:</strong>{' '}
                  <span className="text-red-600 dark:text-red-400">{debugInfo.error}</span>
                </div>
              )}
            </div>
          </div>
        )}
        <MimeDiagnostic networkDiagnostic={networkDiagnostic} mimeErrors={mimeErrors} />
        <RealTimeMonitor />
        {logs.length > 0 && (
          <div className="mb-6 p-4 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-neutral-5 dark:bg-neutral-85">
            <h2 className="text-xl font-semibold mb-3 text-neutral-90 dark:text-white">{t('logs')}</h2>
            <div className="max-h-64 overflow-y-auto">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className={`text-xs font-mono p-2 mb-1 rounded h-fit ${
                    log.includes('[ERROR]')
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                      : log.includes('[WARN]')
                      ? 'bg-yellow-10 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300'
                      : 'bg-primary-10 dark:bg-neutral-80 text-neutral-70 dark:text-neutral-30'
                  }`}
                >
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 p-4 border border-neutral-20 dark:border-neutral-70 rounded-lg bg-primary-60 dark:bg-primary-90/20">
          <h2 className="text-xl font-semibold mb-3 text-neutral-90 dark:text-white">{t('tips')}</h2>
          <ul className="list-disc list-inside space-y-2 text-sm text-neutral-90 dark:text-neutral-30">
            <li>{t('tip1')}</li>
            <li>{t('tip2')}</li>
            <li>
              {t('tip3').split('init')[0]}
              <code className="bg-neutral-30 dark:bg-neutral-80 px-1 rounded text-xs">init</code>
              {t('tip3').split('init')[1]?.split('get')[0]}
              <code className="bg-neutral-30 dark:bg-neutral-80 px-1 rounded text-xs">get</code>
              {t('tip3').split('get')[1]}
            </li>
            <li>{t('tip4')}</li>
            <li>{t('tip5')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
